import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, User, Send } from 'lucide-react';
import { apiService } from '../services/api';

const OPEN_POLL_MS   = 10 * 1000;   // وقتی پنجره‌ی چت بازه: هر ۱۰ ثانیه
const CLOSED_POLL_MS = 60 * 1000;   // وقتی بسته‌ست: هر ۶۰ ثانیه فقط تعداد پاسخ‌های جدید

export const LiveChat = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [unread, setUnread] = useState(0);
  const bottomRef = useRef(null);

  // همون روشی که بقیه‌ی اپ (مثل Header) برای تشخیص لاگین بودن استفاده می‌کنه
  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('user'));
  }, [isOpen]);

  const loadMessages = async () => {
    try {
      const data = await apiService.getSupportMessages();
      setMessages(data);
      setUnread(0);               // سرور با خواندن پیام‌ها، خوانده‌نشده‌ها را صفر می‌کند
    } catch (e) {
      // اگه لود نشد، لیست فعلی رو دست‌نخورده نگه می‌داریم
    } finally {
      setLoaded(true);
    }
  };

  // پنجره‌ی چت باز: پیام‌ها را مرتب تازه کن تا پاسخ ادمین سریع برسد
  useEffect(() => {
    if (!isOpen || !isLoggedIn) return undefined;
    loadMessages();
    const t = setInterval(() => {
      if (!document.hidden) loadMessages();
    }, OPEN_POLL_MS);
    return () => clearInterval(t);
  }, [isOpen, isLoggedIn]);

  // پنجره‌ی چت بسته: فقط بپرس پاسخ جدیدی رسیده یا نه (نشان قرمز روی دکمه)
  useEffect(() => {
    if (isOpen) return undefined;
    const checkUnread = async () => {
      if (!localStorage.getItem('user') || document.hidden) return;
      try {
        const data = await apiService.getSupportUnread();
        setUnread(data.unread || 0);
      } catch (e) {
        // خطای شبکه مهم نیست؛ دفعه‌ی بعد دوباره امتحان می‌شود
      }
    };
    checkUnread();
    const t = setInterval(checkUnread, CLOSED_POLL_MS);
    return () => clearInterval(t);
  }, [isOpen]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    const text = inputMessage.trim();
    if (!text || sending) return;

    setSending(true);
    setInputMessage('');
    try {
      const saved = await apiService.sendSupportMessage(text);
      setMessages(prev => [...prev, saved]);
    } catch (err) {
      alert('ارسال پیام ناموفق بود. دوباره تلاش کنید.');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (iso) =>
    new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' });

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="چت با پشتیبانی"
        className="fixed bottom-6 left-6 w-14 h-14 bg-gradient-to-r from-amber-400 to-yellow-500 rounded-full shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center z-40"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
        {!isOpen && unread > 0 && (
          <span
            data-testid="chat-unread-badge"
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 left-6 w-96 max-w-[90vw] bg-white rounded-lg shadow-2xl z-40 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white p-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-semibold">پشتیبانی آنلاین</h3>
                  <p className="text-sm text-white/80">معمولاً در چند ساعت پاسخ می‌دهیم</p>
                </div>
              </div>
              <button onClick={() => setIsOpen(false)} aria-label="بستن چت">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {!isLoggedIn ? (
            <div className="p-6 text-center text-gray-600 text-sm">
              برای گفتگو با پشتیبانی، لطفاً ابتدا وارد حساب کاربری خود شوید.
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="h-96 overflow-y-auto p-4 space-y-4">
                {loaded && messages.length === 0 && (
                  <p className="text-center text-sm text-gray-400">
                    پیامی ندارید. سوال خودتون رو بنویسید تا در اسرع وقت پاسخ بدیم.
                  </p>
                )}
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${!msg.is_from_admin ? 'justify-start' : 'justify-end'}`}
                  >
                    <div className={`max-w-[80%] ${
                      !msg.is_from_admin
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-amber-100 text-amber-800'
                    } rounded-lg p-3`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                      <p className="text-xs text-gray-500 mt-1">{formatTime(msg.created_at)}</p>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Input */}
              <form onSubmit={sendMessage} className="border-t p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="پیام خود را بنویسید..."
                    className="flex-1 px-4 py-2 border rounded-lg focus:border-amber-500 focus:outline-none"
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    disabled={sending}
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    aria-label="ارسال پیام"
                    className="bg-amber-500 text-white p-2 rounded-lg hover:bg-amber-600 disabled:opacity-50"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};